import { useEffect, useMemo, useRef } from 'react';
import { DataSourceRecord } from 'src/models/common/DataSourceRecord';
import { extractMDAValueFromMDAWorksheetResults, groupMDARecordsByWorksheetAndDatasourceId } from './Utils';
import { parseExcelFromArrayBuffer, ExcelRowConverter } from 'src/utils/ExcelUtils';
import ErrorUtils from "src/utils/ErrorUtils";
import CONSTANTS from 'src/utils/constants';

/**
 * MDA Results row data with simplified array-based structure
 */
interface MDAResultsRow {
    row: (string | number | undefined)[];
}

/**
 * Simplified converter for MDA results data
 */
const mdaResultsConverter: ExcelRowConverter<MDAResultsRow> = (rowData: any[], headers: string[], rowIndex: number): MDAResultsRow => {
    // Convert row data to simplified array format
    const row: (string | number | undefined)[] = headers.map((header, columnIndex) => {
        const cellValue = rowData[columnIndex];
        
        // Handle different data types
        if (cellValue === null || cellValue === undefined) {
            return undefined;
        }
        
        return cellValue;
    });

    return {
        row
    };
};

interface UseMDAValueLoaderProps {
    dataSourceRecords: DataSourceRecord[];
    onRecordUpdate: (datasourceId: string, updates: Partial<DataSourceRecord>) => void;
}

export function useMDAValueLoader({ 
    dataSourceRecords,
    onRecordUpdate 
}: UseMDAValueLoaderProps) {

    // Track processed record IDs to prevent duplicate processing
    const processedRecordsRef = useRef(new Set<string>());

    // Filter records that need MDA value loading
    const datasourceRecordsToBeUpdated = useMemo(() =>
        dataSourceRecords.filter(record =>
            record.datasource === CONSTANTS.DATA_SOURCE_TYPES.MULTIDIMENSIONAL_ALLOCATION_WORKSHEET &&
            !!record.multiAllocationWorksheetId && // Only load when existing record.
            !record.value && // Only load if value is not present
            !processedRecordsRef.current.has(record.datasourceId) // Don't reprocess
        ), [dataSourceRecords]);

    // Group records by worksheetId + datasourceId, use any S3 URL from the group as map key
    const urlToRecordsMap = useMemo(() => {
        datasourceRecordsToBeUpdated.forEach(record => {
            processedRecordsRef.current.add(record.datasourceId);
        });
        return groupMDARecordsByWorksheetAndDatasourceId(datasourceRecordsToBeUpdated);
    }, [datasourceRecordsToBeUpdated]);

    useEffect(() => {
        const fetchAllMDAData = async () => {
            for (const [s3Url, records] of urlToRecordsMap) {
                records.forEach(record => {
                    onRecordUpdate(record.datasourceId, { balancePullInProgress: true });
                });

                try {
                    const response = await fetch(s3Url);
                    if (!response.ok) {
                        throw new Error(`Failed to load excel sheet, received http: ${response.status}`);
                    }
                    const arrayBuffer = await response.arrayBuffer();
                    const parseResult = parseExcelFromArrayBuffer<MDAResultsRow>(
                        arrayBuffer,
                        mdaResultsConverter,
                        { defaultValue: "", sheetIndex: 1, dataStartRowIndex: 0 }
                    );

                    // Process all records that share this S3 URL
                    records.forEach(record => {
                        try {
                            const calcResult = extractMDAValueFromMDAWorksheetResults(
                                parseResult.data,
                                record.multiAllocationWorksheetValueIdentifier!
                            );
                            
                            if (calcResult.error) {
                                const errors = new Map<string, string>();
                                errors.set('MDA', calcResult.error);
                                onRecordUpdate(record.datasourceId, {
                                    errors,
                                    balancePullInProgress: false
                                });
                            } else {
                                onRecordUpdate(record.datasourceId, {
                                    value: calcResult.calculatedValue.toString(),
                                    balancePullInProgress: false
                                });
                            }
                        } catch (error) {
                            const errors = new Map<string, string>();
                            errors.set('MDA', ErrorUtils.getMessage(error));
                            onRecordUpdate(record.datasourceId, {
                                errors,
                                balancePullInProgress: false
                            });
                        }
                    });

                } catch (error) {
                    const errorMessage = ErrorUtils.getMessage(error);
                    records.forEach(record => {
                        const errors = new Map<string, string>();
                        errors.set('MDA', errorMessage);
                        onRecordUpdate(record.datasourceId, {
                            errors,
                            balancePullInProgress: false
                        });
                    });
                }
            }
        };

        if (urlToRecordsMap.size > 0) {
            fetchAllMDAData();
        }
    }, [urlToRecordsMap, onRecordUpdate]);
}
